import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Grid,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    CircularProgress,
    Tooltip,
    Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { getEarliestDeliveryDateWithPayload } from '../modules/prodplanningdata';
import OrderItem from './orderitem';
import { IsProductStandard } from '../common/productdataparse';
import { uniq, some, maxBy, isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Order from '../domain/order';

const warningBaseStyle = {
    verticalAlign: 'middle',
    display: 'inline-flex',
    fontSize: '1.25em',
};

// Maps the field keys returned by orderitem.jsx's getInputErrors() to the i18n keys
// used to label those fields elsewhere in the UI, so the missing-fields tooltip can
// reuse the same wording the user already sees on the corresponding form fields.
const MISSING_FIELD_LABEL_KEYS = {
    toimitusaika: 'general.deliveryDate',
    aikainentoimitusaika: 'order.earliestDeliveryDate',
    toimitusehdot: 'parameterCategories.DeliveryTerms',
    'tilaaja-nimi': 'order.ordererName',
    'tilaaja-puh': 'order.deliveryRecipientTel',
    'order-reference': 'general.yourReference',
    'confirmation-email': 'order.confirmationEmail',
    deliveryMethod: 'parameterCategories.DeliveryMethod',
    deliveryAddress: 'general.deliveryAddress',
    billingInformation: 'general.invoicingInfo',
    palletInformation: 'order.palletGroup',
};

export default function OrderItemContainer(props) {
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState(false);
    const [expanding, setExpanding] = useState(false);
    const [formOk, setFormOk] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [containsNonStandardProducts, setContainsNonStandardProducts] = useState(false);
    const portfolios = useSelector((state) => state.portfoliodata.portfolioDescriptions);
    const [earliestDeliveryDate, setEarliestDeliveryDate] = useState(null);
    const [earliestDeliveryDateAvailable, setEarliestDeliveryDateAvailable] = useState(false);
    const [earliestDeliveryDateDetails, setEarliestDeliveryDateDetails] = useState(null);

    const deliveryDateInfosForOrders = useSelector((state) => state.prodplanningdata.deliveryDateInfosForOrders);

    const [portfolio, setPortfolio] = useState({});
    const order = props.orders[0];
    const orders = props.orders;
    const { t } = useTranslation();

    const onClickCheckbox = (e) => {
        e.stopPropagation();
        props.selectOrder(order.id);
    };

    useEffect(() => {
        if (expanded && !props.combineOrders) {
            if (!deliveryDateInfosForOrders[order.id]) {
                getEarliestDeliveryDateWithPayload(orders)(dispatch);
            } else {
                if (
                    some(deliveryDateInfosForOrders[order.id], (t) => {
                        return t.earliestDeliveryDate == null;
                    })
                ) {
                    setEarliestDeliveryDate(null);
                    setEarliestDeliveryDateDetails(deliveryDateInfosForOrders[order.id]);
                } else {
                    setEarliestDeliveryDate(
                        maxBy(deliveryDateInfosForOrders[order.id], (t) => dayjs(t.earliestDeliveryDate).valueOf())
                            .earliestDeliveryDate
                    );
                    setEarliestDeliveryDateDetails(deliveryDateInfosForOrders[order.id]);
                }
                setEarliestDeliveryDateAvailable(true);
            }
        }
    }, [order, expanded, props.combineOrders, deliveryDateInfosForOrders, dispatch, orders]);

    useEffect(() => {
        if (order && portfolios && portfolios.length > 0) {
            setPortfolio(portfolios.find((p) => p.id === order.portfolioId));
        }
        if (order && !props.customerView) {
            // TODO: miten non-standard käsittely ulotetaan sarjoihin, vai pitääkö?
            setContainsNonStandardProducts(
                some(
                    order.orderedFilters.filter((f) => f.product).map((m) => m.product.productName),
                    (productName) => {
                        return !IsProductStandard(productName);
                    }
                )
            );
        }
    }, [props.orders, portfolios, order, props.customerView]);

    const onExpand = async () => {
        if (!expanded) {
            setExpanding(true);
        }
        setExpanded(!expanded);
        setExpanding(false);
    };

    const updateOrderFormState = (state, orderId, errors) => {
        setFormOk(state);
        // Avoid triggering a re-render (and re-triggering the effect that calls this
        // function) when the error details haven't actually changed - getInputErrors()
        // returns a brand new object on every call, which would otherwise cause an
        // infinite render loop between this container and OrderItem.
        setFormErrors((prev) => (isEqual(prev, errors) ? prev : errors || {}));
        props.updateOrderFormState(state, orderId);
    };

    const getPalletGroups = () => {
        if (order.orderedFilters && order.orderedFilters.length > 0) {
            return uniq(
                order.orderedFilters.map((fo) => {
                    return fo.palletInformation;
                })
            ).join(',');
        }
        return '-';
    };

    const editPalletInfo = (e) => {
        e.stopPropagation();
        props.editOrderPalletInformation(order.id);
    };

    const editFoPalletInfo = (foIds) => {
        props.editFoPalletInformation(order.id, foIds);
    };

    const getMissingFieldLabels = () => {
        return Object.keys(formErrors)
            .filter((k) => formErrors[k])
            .map((k) => t(MISSING_FIELD_LABEL_KEYS[k] || k));
    };

    const getOrderTitle = () => {
        return order.location ? order.location.name : order.deliveryRecipient;
    };

    const orderChanged = (order) => {
        props.orderChanged(order);
    };

    const deliveryTimeMissing = () => {
        if (props.orders[0].orderType === Order.OrderTypeUrgent()) {
            return !props.orders[0].deliveryTime;
        }
        return !props.orders[0].deliveryTime || !props.orders[0].earlyDeliveryTime;
    };

    return (
        <Grid container sx={{ width: '100%', mb: '0.5em' }}>
            <Grid item xs={12}>
                <Accordion expanded={expanded}>
                    <AccordionSummary
                        name="accordion-summary"
                        expandIcon={
                            expanding ? <CircularProgress color={'secondary'} size={'1rem'} /> : <ExpandMoreIcon />
                        }
                        onClick={onExpand}>
                        <Grid container>
                            <Grid item xs={12} sm={6}>
                                <Checkbox
                                    name="select-order-item"
                                    checked={Boolean(props.selected)}
                                    sx={{ mr: '7px', p: 0 }}
                                    onClick={onClickCheckbox}
                                />
                                {containsNonStandardProducts && (
                                    <Tooltip title={t('order.orderContainsNonStandardProducts')}>
                                        <WarningIcon
                                            sx={{ ...warningBaseStyle, color: props.selected ? 'orange' : 'gray' }}
                                            name="warningIcon"
                                        />
                                    </Tooltip>
                                )}
                                {props.selected && (
                                    <span>
                                        {(formOk || props.combineOrders) && <span>{getOrderTitle()}</span>}
                                        {!formOk && !props.combineOrders && (
                                            <Tooltip
                                                title={
                                                    t('order.missingMandatoryFieldsTooltip') +
                                                    ': ' +
                                                    getMissingFieldLabels().join(', ')
                                                }>
                                                <span style={{ color: 'red' }}>{getOrderTitle()}&nbsp;*</span>
                                            </Tooltip>
                                        )}
                                    </span>
                                )}
                                {!props.selected && <span style={{ color: 'lightgray' }}>{getOrderTitle()}</span>}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Chip
                                    disabled={!props.selected || deliveryTimeMissing() || props.autosaving}
                                    label={t('order.palletGroup') + ': ' + getPalletGroups()}
                                    variant="outlined"
                                    name={'edit-pallet-info-' + /*props.order.id*/ order.id}
                                    onClick={editPalletInfo}
                                />
                            </Grid>
                        </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                        <OrderItem
                            customerView={props.customerView}
                            key={order.id}
                            orders={props.orders}
                            removeOrder={props.removeOrder}
                            updateOrderFormState={updateOrderFormState}
                            allowPricing={props.allowPricing}
                            customers={props.customers}
                            customersById={props.customersById}
                            earliestDeliveryDate={earliestDeliveryDate}
                            earliestDeliveryDateDetails={earliestDeliveryDateDetails}
                            editOrderedItem={false}
                            parameters={props.parameters}
                            editFoPalletInfo={editFoPalletInfo}
                            portfolio={portfolio}
                            addProductWithCode={props.addProductWithCode}
                            addProductWithId={props.addProductWithId}
                            earliestDeliveryDateAvailable={earliestDeliveryDateAvailable}
                            orderChanged={orderChanged}
                            warehouses={props.warehouses}
                            autosaving={props.autosaving}
                            blockedDeliveryMethodIds={props.blockedDeliveryMethodIds}
                        />
                    </AccordionDetails>
                </Accordion>
            </Grid>
        </Grid>
    );
}
